import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, Pencil, Trash2, Plus, Check, X, Loader2, Circle } from "lucide-react";
import api from "@/lib/api";

const INPUTS = [["", "— unverändert —"], ["dtv", "TV"], ["HDMI1", "HDMI 1"], ["HDMI2", "HDMI 2"], ["HDMI3", "HDMI 3"]];
const APPS = [["", "— keine —"], ["3201907018807", "Netflix"], ["111299001912", "YouTube"], ["3201910019365", "Prime Video"], ["3201901017640", "Disney+"]];
const EMPTY = { name: "", description: "", power: "on", input: "", volume: "", app: "", light_scene_id: "" };
const err = (e) => e?.response?.data?.detail;

const summary = (s) => {
  if (s.power === "off") { return "TV aus"; }
  const p = [s.power === "on" ? "An" : null, s.input, s.volume != null && s.volume !== "" ? `Lautstärke ${s.volume}` : null,
    APPS.find(([id]) => id && id === s.app)?.[1]].filter(Boolean);
  return p.join(" · ") || "—";
};

export default function TVScenes({ onChanged }) {
  const [scenes, setScenes] = useState([]);
  const [lights, setLights] = useState([]);
  const [edit, setEdit] = useState(null);
  const [run, setRun] = useState(null);

  const load = async () => {
    const [s, l] = await Promise.all([api.get("/admin/tv/scenes"), api.get("/admin/tv/light-scenes").catch(() => ({ data: [] }))]);
    setScenes(s.data); setLights(l.data);
  };
  useEffect(() => { load().catch(() => toast.error("Szenen konnten nicht geladen werden")); }, []);

  const finished = useCallback((data) => {
    if (data.result === "ok") { toast.success(`„${data.scene_name}“ ausgeführt`); } else { toast.error(`„${data.scene_name}“ mit Fehlern beendet`); }
    onChanged();
  }, [onChanged]);

  useEffect(() => {
    if (!run || run.state === "finished") { return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/tv/runs/${run.id}`);
        setRun(data);
        if (data.state === "finished") { finished(data); }
      } catch { setRun(null); }
    }, 700);
    return () => clearTimeout(t);
  }, [run, finished]);

  const start = async (s) => {
    try {
      const { data } = await api.post(`/admin/tv/scenes/${s.id}/run`);
      const r = await api.get(`/admin/tv/runs/${data.run_id}`);
      setRun(r.data);
      if (r.data.state === "finished") { finished(r.data); }
    } catch (e) { toast.error(err(e) || "Start fehlgeschlagen"); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Szene „${s.name}“ löschen?`)) { return; }
    try { await api.delete(`/admin/tv/scenes/${s.id}`); toast.success("Gelöscht"); load(); } catch (e) { toast.error(err(e) || "Fehler"); }
  };
  const save = async () => {
    const body = { ...edit, power: edit.power || null, volume: edit.volume === "" || edit.power === "off" ? null : Number(edit.volume) };
    try {
      if (edit.id) { await api.put(`/admin/tv/scenes/${edit.id}`, body); } else { await api.post("/admin/tv/scenes", body); }
      toast.success("Szene gespeichert"); setEdit(null); load();
    } catch (e) { toast.error(typeof err(e) === "string" ? err(e) : "Bitte Eingaben prüfen"); }
  };

  return (
    <div className="space-y-5" data-testid="tv-scenes">
      {run && <RunProgress run={run} onClose={() => setRun(null)} />}
      <div className="flex justify-end">
        <button onClick={() => setEdit({ ...EMPTY })} data-testid="tv-scene-new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black rounded text-[10px] uppercase tracking-widest hover:bg-[#EEDC82]">
          <Plus size={13} /> Neue Szene
        </button>
      </div>
      {edit && <SceneForm edit={edit} setEdit={setEdit} lights={lights} onSave={save} />}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {scenes.map((s) => (
          <div key={s.id} className="card-lux p-5 flex flex-col gap-3" data-testid={`tv-scene-${s.id}`}>
            <div className="font-heading text-xl">{s.name}</div>
            <div className="text-xs text-[#A8A398] flex-1">{summary(s)}{s.light_scene_id ? " · Licht" : ""}</div>
            <div className="flex gap-2">
              <button onClick={() => start(s)} disabled={run && run.state !== "finished"} data-testid={`tv-scene-run-${s.id}`}
                className="flex-1 inline-flex items-center justify-center gap-2 py-2 border border-[#D4AF37] text-[#D4AF37] rounded text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10 disabled:opacity-40">
                <Play size={12} /> Starten
              </button>
              <button onClick={() => setEdit({ ...EMPTY, ...s, volume: s.volume ?? "", power: s.power || "" })} data-testid={`tv-scene-edit-${s.id}`}
                className="px-3 border border-[#2A2A2A] rounded hover:border-[#D4AF37]"><Pencil size={13} /></button>
              <button onClick={() => remove(s)} data-testid={`tv-scene-delete-${s.id}`}
                className="px-3 border border-[#2A2A2A] rounded hover:border-[#7A1921] text-[#EE9CA1]"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const ICON = { pending: Circle, running: Loader2, done: Check, error: X };
const COLOR = { pending: "text-[#575148]", running: "text-[#EEDC82] animate-spin", done: "text-[#7FD49A]", error: "text-[#EE9CA1]" };

function RunProgress({ run, onClose }) {
  return (
    <div className="card-lux p-5 border-[#D4AF37]/40" data-testid="tv-run-progress">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Ablauf · {run.scene_name}</div>
        {run.state === "finished" && <button onClick={onClose} className="text-[#A8A398]" data-testid="tv-run-close"><X size={14} /></button>}
      </div>
      <ol className="space-y-2">
        {run.steps.map((s, i) => {
          const I = ICON[s.state] || Circle;
          return (
            <li key={i} className="flex items-center gap-3 text-sm" data-testid={`tv-run-step-${i}`}>
              <I size={15} className={COLOR[s.state]} />
              <span className="flex-1">{s.label}</span>
              {s.error && <span className="text-[11px] text-[#EE9CA1]">{s.error}</span>}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

const field = "w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none";

function SceneForm({ edit, setEdit, lights, onSave }) {
  const set = (k) => (e) => setEdit({ ...edit, [k]: e.target.value });
  const off = edit.power === "off";
  return (
    <div className="card-lux p-5 grid sm:grid-cols-2 gap-4" data-testid="tv-scene-form">
      <L label="Name"><input value={edit.name} onChange={set("name")} className={field} data-testid="tv-scene-name" /></L>
      <L label="Power">
        <select value={edit.power || ""} onChange={set("power")} className={field} data-testid="tv-scene-power">
          <option value="on">Einschalten</option><option value="off">Ausschalten</option><option value="">Unverändert</option>
        </select>
      </L>
      {!off && <L label="Quelle"><select value={edit.input} onChange={set("input")} className={field} data-testid="tv-scene-input">{INPUTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></L>}
      {!off && <L label="Lautstärke (0–100, leer = unverändert)"><input type="number" min={0} max={100} value={edit.volume} onChange={set("volume")} className={field} data-testid="tv-scene-volume" /></L>}
      {!off && <L label="App"><select value={edit.app} onChange={set("app")} className={field} data-testid="tv-scene-app">{APPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></L>}
      <L label="Licht-Szene (xComfort, optional)">
        <select value={edit.light_scene_id} onChange={set("light_scene_id")} className={field} data-testid="tv-scene-light">
          <option value="">— keine —</option>
          {lights.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </L>
      <div className="sm:col-span-2 text-[11px] text-[#A8A398]">Reihenfolge: Power → 6 s warten → Quelle → Lautstärke → App → Licht</div>
      <div className="sm:col-span-2 flex gap-2 justify-end">
        <button onClick={() => setEdit(null)} className="px-4 py-2 border border-[#2A2A2A] rounded text-[10px] uppercase tracking-widest" data-testid="tv-scene-cancel">Abbrechen</button>
        <button onClick={onSave} disabled={!edit.name.trim()} className="px-4 py-2 bg-[#D4AF37] text-black rounded text-[10px] uppercase tracking-widest disabled:opacity-40" data-testid="tv-scene-save">Speichern</button>
      </div>
    </div>
  );
}

export const L = ({ label, children }) => (
  <label className="block space-y-1.5"><span className="text-[10px] uppercase tracking-[0.25em] text-[#A8A398]">{label}</span>{children}</label>
);
